import { useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { useActivityTracker } from 'common/hooks';
import { useAppSelector } from 'common/hooks/state';
import { selectToken } from 'features/login/authSlice';
import { selectLockEnabled } from 'common/store/lockSlice';
import { safeStorage } from 'common/utils/safeStorage';

/**
 * WebLifecycleHandler - Manages session lock for web platform only
 *
 * This component:
 * - Only activates on web (not native apps which use biometric)
 * - Tracks user activity via useActivityTracker
 * - Automatically locks session after timeout period
 *
 * Should be placed near root of app, inside Redux Provider
 */
export const WebLifecycleHandler: React.FC = () => {
  const isNative = Capacitor.isNativePlatform();
  const token = useAppSelector(selectToken);
  const lockEnabled = useAppSelector(selectLockEnabled);

  // Only enable activity tracking on web when user is logged in and lock is enabled
  const shouldTrack = !isNative && !!token && lockEnabled;

  // Activity tracker handles all the lock logic internally
  useActivityTracker();

  // Log when session lock is active (for debugging)
  useEffect(() => {
    if (shouldTrack) {
      // eslint-disable-next-line no-console
      console.log('[WebLifecycleHandler] Session lock active');
    }
  }, [shouldTrack]);

  // Defence for LockScreen's sign-out path - the one action in this app
  // that does a real full-page navigation (window.location.replace) and
  // unloads a previously-authenticated document, rather than a client-side
  // route change. The browser can restore that unloaded page from its
  // back-forward cache (bfcache) on a later back/forward, instantly
  // resurrecting its entire frozen JS heap - including a stale, still
  // -authenticated Redux store from before credentials were ever cleared -
  // without re-running any React effect or re-reading safeStorage.
  // `pageshow` firing with `persisted: true` is the browser's own signal
  // that this just happened; force a real reload so PersistLogin's boot
  // check runs fresh against the (correctly cleared) persisted state,
  // rather than trusting whatever was frozen in memory. Web only - native
  // apps never unload their single WebView this way.
  useEffect(() => {
    if (isNative) return undefined;

    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        window.location.reload();
      }
    };

    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, [isNative]);

  // This component doesn't render anything - it just runs the hook
  return null;
};

export default WebLifecycleHandler;
